import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { BRAND } from "../brand";
import { useAuth } from "../hooks/useAuth";
import { ErrorBoundary } from "./ErrorBoundary";
import { Spinner } from "./ui";

const PUBLIC_PATHS = ["/login", "/register", "/auth/check-email", "/auth/callback"];

const NAV_ITEMS = [
  { to: "/activities", label: "Activities", testId: "nav-activities-link" },
  { to: "/wellness", label: "Wellness", testId: "nav-wellness-link" },
  { to: "/photos", label: "Photos", testId: "nav-photos-link" },
  { to: "/training", label: "Training", testId: "nav-training-link" },
  { to: "/gear", label: "Gear", testId: "nav-gear-link" },
  { to: "/settings", label: "Settings", testId: "nav-settings-link" },
] as const;

function useDarkMode(): [boolean, () => void] {
  const [dark, setDark] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem("theme") === "dark",
  );

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  return [dark, () => setDark((value) => !value)];
}

export default function AppLayout() {
  const { signedIn, loading, user, signOut } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, toggleDark] = useDarkMode();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (loading) return <Spinner label="Loading your account" />;

  if (!signedIn && !PUBLIC_PATHS.includes(location.pathname)) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "bg-gray-100 text-gray-900 dark:bg-[#252525] dark:text-gray-100"
        : "text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-[#252525] dark:hover:text-gray-100"
    }`;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 font-sans text-gray-900 dark:bg-[#111111] dark:text-gray-100">
      <nav className="sticky top-0 z-20 border-b border-gray-200 bg-white/90 backdrop-blur dark:border-[#2e2e2e] dark:bg-[#1a1a1a]/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/activities" className="flex items-center gap-2" data-testid="nav-brand-link">
            <img src={BRAND.logoPath} alt={BRAND.name} className="h-9 w-9" />
            <div>
              <p className="text-lg font-semibold tracking-tight">{BRAND.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Strength for the long run</p>
            </div>
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            {signedIn &&
              NAV_ITEMS.map((item) => (
                <NavLink key={item.to} to={item.to} className={navClass} data-testid={item.testId}>
                  {item.label}
                </NavLink>
              ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleDark}
              data-testid="layout-theme-toggle-button"
              className="rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-[#252525] dark:hover:text-gray-100"
            >
              {dark ? "Light" : "Dark"}
            </button>
            {signedIn ? (
              <>
                <span className="hidden text-sm text-gray-500 dark:text-gray-400 sm:inline" data-testid="layout-user-label">
                  {user?.displayName ?? user?.email}
                </span>
                <button
                  type="button"
                  onClick={() => void signOut()}
                  data-testid="layout-signout-button"
                  className="rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-[#252525] dark:hover:text-gray-100"
                >
                  Sign out
                </button>
              </>
            ) : (
              <div className="hidden items-center gap-2 sm:flex">
                <Link to="/login" className="text-sm text-gray-600 dark:text-gray-300" data-testid="layout-login-link">
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                  data-testid="layout-register-link"
                >
                  Create account
                </Link>
              </div>
            )}
            {signedIn && (
              <button
                type="button"
                onClick={() => setMenuOpen((value) => !value)}
                data-testid="layout-menu-toggle-button"
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900 md:hidden dark:text-gray-400 dark:hover:bg-[#252525] dark:hover:text-gray-100"
              >
                Menu
              </button>
            )}
          </div>
        </div>
        {signedIn && menuOpen && (
          <div className="border-t border-gray-200 px-4 py-3 md:hidden dark:border-[#2e2e2e]">
            <div className="space-y-1">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.to} to={item.to} className={navClass} data-testid={`${item.testId}-mobile`}>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      <footer className="border-t border-gray-200 bg-white dark:border-[#2e2e2e] dark:bg-[#1a1a1a]">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 text-sm text-gray-500 sm:px-6 lg:px-8 md:flex-row md:items-center md:justify-between dark:text-gray-400">
          <p>{BRAND.footerDisclaimer}</p>
          <a href={`https://${BRAND.sisterDomain}`} className="text-emerald-700 dark:text-emerald-400">
            {BRAND.sisterBrandName} — {BRAND.sisterTagline}
          </a>
        </div>
      </footer>
    </div>
  );
}
